import { useInfiniteScroll } from '@vueuse/core'

import type { Ref } from 'vue'
import type { BadgeKanbanStageStatusColor } from '@shared/ui/kanban'
import type { DisciplineStageLog } from './interfaces'
import type { CustomerBrand } from '@shared/config'
import type { DisciplineStageLogView } from './discipline-stage-logs'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { useDayjs } from '@shared/lib/format'
import { useAdminRoutes } from '@shared/lib/routes'
import { AppRoute } from '@shared/config'

import { useDisciplineStageLogsStore } from './stage-logs/store'
import { useDisciplineStore } from './store'
import { useDisciplineStageLogsApi } from '../api/stage-logs.hook'
import {
  buildDisciplineStageLogView,
  getStageLogActorName,
  isStageLogGroupEnd,
  isStageLogGroupStart,
} from './discipline-stage-logs'
import { DISCIPLINE_STAGE_COLOR } from '../config/discipline-stage-color.const'

const ACTIVATION_DATA_FIELD = 'activation_data'

interface DisciplineStageBadge {
  text: string
  color: BadgeKanbanStageStatusColor
}

/** The legacy kanban customer label: crown, Goal, brand and the first name. */
export interface DisciplineStageLogCustomerBadge {
  firstName: string
  brand: CustomerBrand | null
  url: string | null
  isPremium: boolean
  isGo: boolean
}

export interface DisciplineStageLogRow {
  log: DisciplineStageLog
  view: DisciplineStageLogView
  date: string
  actorName: string | null
  actorUrl: string | null
  /** Activation data the customer changed shows the customer label instead of the actor and the action. */
  customerBadge: DisciplineStageLogCustomerBadge | null
  stage: DisciplineStageBadge | null
  stageFrom: DisciplineStageBadge | null
  stageTo: DisciplineStageBadge | null
  isGroupStart: boolean
  isGroupEnd: boolean
}

interface DisciplineStageLogsReturn {
  rows: ComputedRef<DisciplineStageLogRow[]>
  isLoading: ComputedRef<boolean>
  isLoadingMore: Ref<boolean>
  kanbanPriority: ComputedRef<Record<string, number>>
  fetchLogs: () => Promise<void>
}

const STAGE_FALLBACK_COLOR: BadgeKanbanStageStatusColor = 'gray'

export const useDisciplineStageLogs = (): DisciplineStageLogsReturn => {
  const route = useRoute()
  const dayjs = useDayjs()
  const store = useDisciplineStageLogsStore()
  const listsStore = useListsStore()
  const disciplineStore = useDisciplineStore()

  const isLoadingMore = ref(false)

  const disciplineId = (): string => route.params.id as string

  const stageLabels = computed<Record<string, string>>(() => listsStore.getLists?.discipline.stage ?? {})
  const kanbanPriority = computed<Record<string, number>>(() => listsStore.getLists?.kanban_priority ?? {})
  const isLoading = computed<boolean>(() => store.isLoading && !isLoadingMore.value)

  const toStageBadge = (stage: string | null): DisciplineStageBadge | null => stage
    ? { text: stageLabels.value[stage] ?? stage, color: DISCIPLINE_STAGE_COLOR[stage] ?? STAGE_FALLBACK_COLOR }
    : null

  /** Users and customers open their app pages; tutors are not migrated yet and open the main-app page. */
  const getActorUrl = (log: DisciplineStageLog): string | null => {
    const actorId = log.actor?.id
    if (!actorId) return null

    if (log.actor_type === 'customers') return resolveUrl(AppRoute.CustomersShow, { id: actorId })
    if (log.actor_type === 'tutors') return useAdminRoutes('admin.tutor.show', { tutor_id: actorId })

    return resolveUrl(AppRoute.UserEdit, { id: actorId })
  }

  const getCustomerBadge = (log: DisciplineStageLog): DisciplineStageLogCustomerBadge | null => {
    if (log.actor_type !== 'customers' || log.field !== ACTIVATION_DATA_FIELD || !log.actor) return null

    return {
      firstName: log.actor.first_name ?? log.actor.full_name ?? '',
      brand: log.actor.brand ?? null,
      url: getActorUrl(log),
      isPremium: disciplineStore.isCustomerPremium,
      isGo: disciplineStore.isCustomerGo,
    }
  }

  const rows = computed<DisciplineStageLogRow[]>(() => {
    const lists = listsStore.getLists
    const viewLists = {
      expectations: { ...lists?.activation.expectations.all, ...lists?.activation.expectations.activation_goals },
      channels: lists?.stage_comments.channel.all ?? {},
      reach: lists?.stage_comments.reach ?? {},
      reasons: lists?.reason.all ?? {},
      subReasons: lists?.sub_reason.all ?? {},
    }
    const logs = store.getLogs

    return logs.map((log, index) => {
      const view = buildDisciplineStageLogView(log, viewLists)

      return {
        log,
        view,
        date: dayjs(log.updated_at).format(DateFormat.KanbanDate),
        actorName: getStageLogActorName(log),
        actorUrl: getActorUrl(log),
        customerBadge: getCustomerBadge(log),
        stage: toStageBadge(log.stage),
        stageFrom: toStageBadge(view.stages?.from ?? null),
        stageTo: toStageBadge(view.stages?.to ?? null),
        isGroupStart: isStageLogGroupStart(logs, index),
        isGroupEnd: isStageLogGroupEnd(logs, index),
      }
    })
  })

  const fetchLogs = async (): Promise<void> => {
    await useDisciplineStageLogsApi({ disciplineId: disciplineId() })
  }

  const loadMore = async (): Promise<void> => {
    const cursor = store.getNextCursor
    if (!cursor) return

    isLoadingMore.value = true

    try {
      await useDisciplineStageLogsApi({ disciplineId: disciplineId(), cursor })
    } finally {
      isLoadingMore.value = false
    }
  }

  useInfiniteScroll(window, loadMore, {
    distance: 200,
    canLoadMore: () => store.hasMore && !store.isLoading,
  })

  return {
    rows,
    isLoading,
    isLoadingMore,
    kanbanPriority,
    fetchLogs,
  }
}
